import { useContext, useEffect } from "react";

import {
  BooleanParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import { FETCH_SETTINGS_DETAILS } from "@/queries";
import { useNorthbeamQuery } from "@hooks/http";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import { type FetchSettingsDetails } from "@north-beam/nb-client-side";
import { SideNavContext, type SubNavItem } from "@pages/onboarding";
import { Steps } from "@pages/onboarding/steps";
import { ShopifyConnectionCard } from "@pages/settings/connections/shopify";

import { Error } from "../../error";
import { Loading } from "../../loading";
import { ConfirmCheckbox } from "../components/confirm-checkbox";
import { InfoPanel, ReadDocumentationButton } from "../components/info-panel";
import { OrderedList } from "../components/ordered-list";

import {
  OrderTagExclusionDocPanel,
  OrderTagExclusions,
} from "./order-tag-exclusions";
import { OrderTagsConfirmation } from "./order-tags-confirmation";
import { type OrdersApiStepMetadata } from "./step-metadata";

const SUB_NAV_ITEMS: SubNavItem[] = [
  {
    label: "8a. Please download the Northbeam app to your Shopify store",
    subStep: "a",
  },
  {
    label: "8b. Tell us if your brand will use order tags",
    subStep: "b",
  },
  {
    label: "8c. Add order tags in Shopify",
    subStep: "c",
  },
  {
    label: "8d. Configure Northbeam order exclusions (optional)",
    subStep: "d",
  },
];

const Shopify = () => {
  const { data, loading, error } = useNorthbeamQuery<FetchSettingsDetails>(
    FETCH_SETTINGS_DETAILS,
    {},
  );

  return (
    <>
      {data ? (
        <ShopifyConnectionCard integrationDetails={data.integrationDetails} />
      ) : error ? (
        <Error content="There was an error loading your settings." />
      ) : loading ? (
        <Loading />
      ) : (
        <></>
      )}
    </>
  );
};

export const ShopifyOrderTags = ({
  aTitleRef,
  bTitleRef,
  cTitleRef,
  dTitleRef,
  stepMetadata,
  updateStepMetadata,
  isMarkedComplete,
  goToNamedStep,
}: {
  aTitleRef: (node: any) => void;
  bTitleRef: (node: any) => void;
  cTitleRef: (node: any) => void;
  dTitleRef: (node: any) => void;
  stepMetadata: OrdersApiStepMetadata;
  updateStepMetadata: (data: OrdersApiStepMetadata) => void;
  isMarkedComplete?: boolean;
  goToNamedStep?: (step: string) => void;
}) => {
  const { setSubNavItems } = useContext(SideNavContext);

  useEffect(() => {
    if (stepMetadata.doNotUseOrderTags) {
      setSubNavItems(SUB_NAV_ITEMS.slice(0, 2));
    } else {
      setSubNavItems(SUB_NAV_ITEMS);
    }
  }, [setSubNavItems, stepMetadata]);

  const [_, setSearchParams] = useQueryParams({
    subStep: withDefault(StringParam, ""),
    step: withDefault(StringParam, ""),
    noScroll: withDefault(BooleanParam, false),
  });

  const { designElementManager } = useDesignElementManager();
  return (
    <>
      <div
        className="w-full px-10 pb-20 rounded-tl-lg rounded-tr-lg flex-col justify-start items-start inline-flex gap-10 scroll-mt-10
          [&_h1]:m-0 [&_h1]:mb-2 [&_h1]:p-0 [&_h1]:font-semibold [&_h1]:text-2xl
          [&_h2]:m-0 [&_h2]:p-0 [&_h2]:font-semibold [&_h2]:text-lg [&_h2]:leading-6
          [&_p]:m-0 [&_p]:p-0
        "
      >
        <div
          ref={aTitleRef}
          className="py-10 flex-col justify-start items-start gap-6 inline-flex border-b border-gray-0 scroll-mt-10"
        >
          <div className="text-slate-900 text-2xl font-semibold leading-loose">
            8a. Please download the Northbeam app to your Shopify store
          </div>
          <a
            href="https://apps.shopify.com/north-beam"
            target="_blank"
            className="px-4 py-1.5 bg-cosmic rounded-2xl border
              justify-center items-center gap-2 inline-flex text-white no-underline uppercase mb-6 text-xs font-semibold"
            rel="noreferrer"
          >
            Open shopify in a new tab
            <i className="fa-solid fa-share" style={{ color: "white" }}></i>
          </a>
          <Shopify />
          <div className="self-stretch h-28 p-6 bg-cosmic-5 rounded-lg border border-neutral-300 flex-col justify-end items-start gap-4 flex">
            <div className="self-stretch justify-start items-center gap-2 inline-flex">
              <div className="w-[20px] h-[20px] bg-cosmic border border-black rounded-full flex justify-center items-center">
                <i
                  className="fa-solid fa-user-plus fa-2xs"
                  style={{ color: "#ffffff" }}
                ></i>
              </div>
              <div className="grow shrink basis-0 text-lg font-semibold leading-normal">
                Not the account manager for Shopify?
              </div>
            </div>
            <button
              onClick={() => {
                setSearchParams({
                  step: Steps.ACCOUNT_MANAGERS,
                  subStep: undefined,
                });
                goToNamedStep && goToNamedStep("account-managers");
              }}
              className="justify-start items-end gap-4 inline-flex"
            >
              <div className="px-4 py-1.5 bg-white rounded-2xl border border-neutral-300 justify-center items-center gap-2.5 flex">
                <i
                  style={{ color: "#7081FF" }}
                  className="fa-light fa-envelope fa-sm"
                ></i>
                <div className="text-slate-950 text-xs font-semibold uppercase leading-tight tracking-tight">
                  Invite someone to help with onboarding
                </div>
              </div>
            </button>
          </div>
        </div>
        <div
          ref={bTitleRef}
          className="self-stretch justify-start items-start inline-flex flex-col scroll-mt-20"
        >
          <OrderTagsConfirmation
            isMarkedComplete={isMarkedComplete}
            stepMetadata={stepMetadata}
            updateStepMetadata={updateStepMetadata}
          />
        </div>
        {stepMetadata.doNotUseOrderTags || (
          <>
            <div
              ref={cTitleRef}
              className="w-full py-10 border-t flex-col justify-start items-start inline-flex scroll-mt-10"
            >
              <div className="w-full inline-flex flex-col gap-10">
                <div>
                  <h1>{SUB_NAV_ITEMS[2].label as string}</h1>
                  <p className="m-0 mt-2 p-0 text-sm">
                    You only need to add additional tags that you don't already
                    have setup.
                  </p>
                </div>
                <div className="pb-10 flex flex-col text-sm">
                  <h2>1. Create order tags</h2>
                  <div className="mt-2 mb-4 flex items-center gap-2">
                    <b>Note:</b>
                    {designElementManager
                      .getDesignElement("recharge")
                      .getComponent()}
                    <span>
                      Recharge customers don’t need to create order tags for
                      subscriptions. You should still create order tags for
                      other cases, like returns.
                    </span>
                  </div>
                  <OrderedList variant="upper-alpha" className="mb-6">
                    <>Log in to Shopify</>
                    <>From your Shopify Admin, click on ‘Orders’</>
                    <>Select an order</>
                    <>
                      In the ‘Tags’ section, enter the name of the tag you want
                      to add. If you enter the name of a new tag - it will
                      automatically be created for future use across all orders.{" "}
                    </>
                    <>Click save</>
                    <>
                      Once you’ve created order tags, you can bulk update orders
                      from the ‘Orders’ page
                    </>
                  </OrderedList>
                  <InfoPanel label="Need help configuring order tagging?">
                    <ReadDocumentationButton docUrl="https://help.shopify.com/en/manual/shopify-flow/reference/actions/add-order-tags" />
                  </InfoPanel>
                </div>
                <div className="pt-10 flex flex-col gap-4 border-t">
                  <h2>2. Confirm you’ve updated Shopify order tags</h2>
                  <ConfirmCheckbox
                    disabled={isMarkedComplete}
                    checked={!!stepMetadata.apiOrderTagsConfirmed}
                    id="confirm-shopify-order-tags"
                    onChange={(checked) =>
                      updateStepMetadata({
                        ...stepMetadata,
                        apiOrderTagsConfirmed: checked,
                      })
                    }
                    showSuccess
                  >
                    I’m done setting up Shopify order tags
                  </ConfirmCheckbox>
                </div>
              </div>
            </div>
            <div
              ref={dTitleRef}
              className="w-full py-10 border-t flex-col justify-start items-start inline-flex scroll-mt-10"
            >
              <div className="self-stretch justify-start items-start inline-flex flex-col">
                <h1>{SUB_NAV_ITEMS[3].label as string}</h1>
                <p className="text-sm font-normal leading-normal">
                  Use order tags to exclude certain purchases from attribution.
                </p>
                <OrderTagExclusionDocPanel className="mt-2" />
              </div>
              <div className="self-stretch py-10 inline-flex flex-col gap-4">
                <h2>1. Configure Order Exclusions (optional)</h2>
                <OrderTagExclusions disabled={isMarkedComplete} />
              </div>
              <div className="self-stretch py-10 border-t inline-flex flex-col gap-4">
                <h2>2. Confirm you’re finished with Order Exclusions</h2>
                <ConfirmCheckbox
                  disabled={isMarkedComplete}
                  id="confirm-shopify-order-exclusions"
                  checked={!!stepMetadata.orderExclusionsComplete}
                  onChange={(checked) =>
                    updateStepMetadata({
                      ...stepMetadata,
                      orderExclusionsComplete: checked,
                    })
                  }
                  showSuccess
                >
                  I’m finished with Order Exclusions
                </ConfirmCheckbox>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};
